import { lazyComponent } from 'suspendable';

import { ApplicationPreloadData } from './pages/Application/data';
import { ClientProfilePreloadData } from 'app/pages/ClientProfile/data';
import { ClientsPreloadData } from './pages/Clients/data';
import { DefaultPreloadData } from 'app/layouts/Default/data';
import { HomePreloadData } from './pages/Home/data';
import { InvoiceCreatePreloadData } from './pages/InvoiceCreate/data';
import { InvoicesPreloadData } from './pages/Invoices/data';
import { PaymentsPreloadData } from './pages/Payments/data';
import { ProfileSetupPreloadData } from './pages/ProfileSetup/data';
import { RegisterPreloadData } from 'app/pages/AuthPages/Register/data';
import { ResetPasswordPreloadData } from './pages/AuthPages/ResetPasswordPage/data';
import { SchedulePreloadData } from './pages/MySchedule/data';
import { SessionsPreloadData } from './pages/Sessions/data';
import { SettingsAccountPreloadData } from 'app/pages/Settings/SettingsAccount/data';
import { SettingsProfilePreloadData } from 'app/pages/Settings/SettingsProfile/data';
import { SettingsClientPreferencesPreloadData } from 'app/pages/Settings/SettingsPreferences/data';
import { SettingsIntegrationsPreloadData } from 'app/pages/Settings/SettingsIntegrations/data';
import { SignUpInvitePreloadData } from './pages/AuthPages/SignUpInvite/data';
import { SignUpPreloadData } from './pages/AuthPages/SignUp/data';
import { TalkPaymentsPreloadData } from 'app/pages/TalkPayments/data';
import { TeamPreloadData } from 'app/pages/Team/data';
import { TeamFeesPreloadData } from 'app/pages/TeamFees/data';
import { TeamMembersPreloadData } from './pages/TeamMembers/data';
import { TeamMembershipsPreloadData } from './pages/TeamMemberships/data';

const routes = [
  /**
   * 404
   */
  {
    component: lazyComponent(() => import('app/layouts/Default'), { autoRetry: true }),
    preloadData: params => DefaultPreloadData(params),
    routes: [
      /**
       * Home
       */
      {
        path: '/',
        component: lazyComponent(() => import('app/pages/Home'), { autoRetry: true }),
        authRequired: true,
        preloadData: () => HomePreloadData(),
      },
      /**
       * Application Pages
       */
      {
        path: '/setup',
        component: lazyComponent(() => import('app/pages/Application'), { autoRetry: true }),
        authRequired: true,
        preloadData: () => ApplicationPreloadData(),
      },

      /**
       * Profile Pages
       */
      {
        path: '/profile',
        component: lazyComponent(() => import('app/pages/Settings/SettingsProfile'), {
          autoRetry: true,
        }),
        preloadData: params => SettingsProfilePreloadData(params),
        authRequired: true,
      },
      /**
       * Appointments
       */
      {
        path: '/appointments/calendar',
        component: lazyComponent(() => import('app/pages/MySchedule'), {
          autoRetry: true,
        }),
        authRequired: true,
        preloadData: (params, search) => SchedulePreloadData({ params, search }),
      },
      {
        path: '/appointments/calendar/request/:requestId',
        component: lazyComponent(() => import('app/pages/MySchedule'), {
          autoRetry: true,
        }),
        authRequired: true,
        preloadData: (params, search) => SchedulePreloadData({ params, search }),
      },
      /**
       * Clients
       */
      {
        path: '/clients',
        component: lazyComponent(() => import('app/pages/Clients'), {
          autoRetry: true,
        }),
        authRequired: true,
        preloadData: () => ClientsPreloadData(),
      },
      /**
       * Client Profile Page
       */
      {
        path: '/clients/:clientId/contact_details',
        component: lazyComponent(() => import('app/pages/ClientProfile'), {
          autoRetry: true,
        }),
        authRequired: true,
        preloadData: params => ClientProfilePreloadData(params),
      },
      {
        path: '/clients/:clientId/sessions',
        component: lazyComponent(() => import('app/pages/ClientProfile'), {
          autoRetry: true,
        }),
        authRequired: true,
        preloadData: params => ClientProfilePreloadData(params),
      },
      /**
       * Sessions
       */
      {
        path: '/sessions',
        component: lazyComponent(() => import('app/pages/Sessions'), {
          autoRetry: true,
        }),
        authRequired: true,
        preloadData: () => SessionsPreloadData(),
      },
      /**
       * Payments
       */
      {
        path: '/payments',
        component: lazyComponent(() => import('app/pages/TalkPayments'), {
          autoRetry: true,
        }),
        authRequired: true,
        preloadData: () => TalkPaymentsPreloadData(),
      },
      /**
       * Team Members
       */
      {
        path: '/team/members',
        component: lazyComponent(() => import('app/pages/TeamMembers'), {
          autoRetry: true,
        }),
        preloadData: params => TeamMembersPreloadData(params),
        authRequired: true,
      },
      {
        path: '/team/members/invitations',
        component: lazyComponent(() => import('app/pages/TeamMembers'), {
          autoRetry: true,
        }),
        preloadData: params => TeamMembersPreloadData(params),
        authRequired: true,
      },
      {
        path: '/team/my-team/all',
        component: lazyComponent(() => import('app/pages/TeamMembers'), {
          autoRetry: true,
        }),
        // preloadData: params => TeamMembersPreloadData(params),
        authRequired: true,
      },
      {
        path: '/team/memberships',
        component: lazyComponent(() => import('app/pages/TeamMemberships'), {
          autoRetry: true,
        }),
        preloadData: params => TeamMembershipsPreloadData(params),
        authRequired: true,
      },
      {
        path: '/team/memberships/invitations',
        component: lazyComponent(() => import('app/pages/TeamMemberships'), {
          autoRetry: true,
        }),
        preloadData: params => TeamMembershipsPreloadData(params),
        authRequired: true,
      },
      {
        path: '/team/fees',
        component: lazyComponent(() => import('app/pages/TeamFees'), {
          autoRetry: true,
        }),
        preloadData: () => TeamFeesPreloadData(),
        authRequired: true,
      },
      /**
       * Finances
       */
      {
        path: '/sales/invoices/all',
        component: lazyComponent(() => import('app/pages/Invoices'), {
          autoRetry: true,
        }),
        preloadData: () => InvoicesPreloadData(),
        authRequired: true,
      },
      {
        path: '/sales/payments/all',
        component: lazyComponent(() => import('app/pages/Payments'), {
          autoRetry: true,
        }),
        preloadData: () => PaymentsPreloadData(),
        authRequired: true,
      },
      {
        path: '/sales/invoices/add-invoice',
        component: lazyComponent(() => import('app/pages/InvoiceCreate'), {
          autoRetry: true,
        }),
        preloadData: () => InvoiceCreatePreloadData(),
        authRequired: true,
      },
      /**
       * Teams
       */
      {
        path: '/teams/:team',
        component: lazyComponent(() => import('app/pages/Team'), {
          autoRetry: true,
        }),
        preloadData: params => TeamPreloadData(params),
        authRequired: true,
      },
      /**
       * Settings
       */
      {
        path: '/settings/preferences',
        component: lazyComponent(() => import('app/pages/Settings/SettingsPreferences'), {
          autoRetry: true,
        }),
        preloadData: params => SettingsClientPreferencesPreloadData(params),
        authRequired: true,
      },
      {
        path: '/settings/security',
        component: lazyComponent(() => import('app/pages/Settings/SettingsSecurity'), {
          autoRetry: true,
        }),
        authRequired: true,
      },
      {
        path: '/settings/account',
        component: lazyComponent(() => import('app/pages/Settings/SettingsAccount'), {
          autoRetry: true,
        }),
        preloadData: params => SettingsAccountPreloadData(params),
        authRequired: true,
      },
      {
        path: '/settings/integrations',
        component: lazyComponent(() => import('app/pages/Settings/SettingsIntegrations'), {
          autoRetry: true,
        }),
        preloadData: params => SettingsIntegrationsPreloadData(params),
        authRequired: true,
      },
      {
        path: '/settings/integrations/zoom',
        component: lazyComponent(() => import('app/pages/Settings/SettingsIntegrations'), {
          autoRetry: true,
        }),
        preloadData: params => SettingsIntegrationsPreloadData(params),
        authRequired: true,
      },
      {
        path: '/settings/integrations/google',
        component: lazyComponent(() => import('app/pages/Settings/SettingsIntegrations'), {
          autoRetry: true,
        }),
        preloadData: params => SettingsIntegrationsPreloadData(params),
        authRequired: true,
      },
    ],
  },
  {
    component: lazyComponent(() => import('app/layouts/Minimal'), { autoRetry: true }),
    routes: [
      /**
       * Auth
       */
      {
        path: '/login',
        component: lazyComponent(() => import('app/pages/AuthPages/Login'), { autoRetry: true }),
        authRequired: false,
      },
      {
        path: '/sign-up',
        component: lazyComponent(() => import('app/pages/AuthPages/SignUp'), { autoRetry: true }),
        preloadData: params => SignUpPreloadData(params),
        authRequired: false,
      },
      {
        path: '/register/:token',
        component: lazyComponent(() => import('app/pages/AuthPages/Register'), { autoRetry: true }),
        preloadData: (params: { token: string }) => RegisterPreloadData(params),
        authRequired: false,
      },
      {
        path: '/sign-up/warm',
        component: lazyComponent(() => import('app/pages/AuthPages/SignUpInvite'), { autoRetry: true }),
        preloadData: () => SignUpInvitePreloadData(),
        authRequired: false,
      },
      {
        path: '/forgot-password',
        component: lazyComponent(() => import('app/pages/ForgotPassword'), { autoRetry: true }),
        authRequired: false,
      },
      {
        path: '/reset-password/:token',
        component: lazyComponent(() => import('app/pages/AuthPages/ResetPasswordPage'), { autoRetry: true }),
        preloadData: (params: { token: string }) => ResetPasswordPreloadData(params),
        authRequired: false,
      },
      /**
       * Profile Setup
       */
      {
        path: '/profile/setup',
        component: lazyComponent(() => import('app/pages/ProfileSetup'), {
          autoRetry: true,
        }),
        preloadData: params => ProfileSetupPreloadData(),
        authRequired: true,
      },
      /**
       * Miscellaneous
       */
      {
        path: '/callback',
        component: lazyComponent(() => import('./callback'), { autoRetry: true }),
        authRequired: false,
      },
    ],
  },
  {
    component: lazyComponent(() => import('app/pages/Error'), { autoRetry: true }),
  },
];

export default routes;
